import React, { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { Box, Typography, Modal, Button } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useAppDispatch, useAppSelector } from '../../../../../reduxStore/redux-hooks';
import {
  toggleModal,
  updateFileUploadState,
  updateFileSelectionState,
  updateDatasetState,
  setFiles,
  setSuccessResponseModal,
} from '../../../../../reduxStore/slices/FileUploadSlice';
import {
  currentDataSet,
  refreshDatasetList,
  selectedDataSet,
  setEditDataset,
  setOpenCloseDatasetModal,
} from '../../../../../reduxStore/slices/DataSetSlice';
import {
  AdvanceSummaryResponse,
  saveAdvanceSummaryResponse,
  saveSowCommitAiQueryResponse,
  saveSowPredictorQueryResponse,
  setDisableCustomApps,
  SowCommitAiQueryResponse,
  SowPredictorQueryResponse,
} from '../../../../../reduxStore/slices/CustomAppsSlice';
import { showUserActionContentOnChat } from 'reduxStore/slices/NotifyUserActionContentSlice';
import UsePageNavigation from '../../../../sharedComponents/customHooks/usePageNavigation/UsePageNavigation';
import { UseTrackedNavigate } from '../../../../sharedComponents/customHooks/useTrackedNavigate/UseTrackedNavigate';
import RadioSelect from './RadioSelect';
import NewDataset from './newDataset/NewDataset';
import ExistingDataset from './existingDataset/ExistingDataset';
import Api, { ApiError, SkillId } from '../../../../../data/api/Api';
import {
  chatPageNavigation,
  welcomePageNavigation,
  AppSkillId,
  customAppsSkillIds,
  MAX_LENGTH_DATASET_NAME,
} from '../../../../staticComponents/StaticHtmlGenerator';
import SharePointDataset from './SharePointDataset/SharePointDataset';
import ExistingSharePointDataset, { SharePointAttributes } from './existingSharePointdataset/ExistingSharePointDatset';
import { isValidUrl, showSnackbarErrorMessage } from '../../../../../components/staticComponents/staticUtiles';
import ExistingDatasetRadioSelect from './ExistingDatasetRadioSelect';
import { findEmptyFileName } from 'components/staticComponents/staticUtiles';
import { setIsSharePointSelected } from '../../../../../reduxStore/slices/FileUploadSlice';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { LogButtonEventToGA } from '../../../../../components/sharedComponents/googleAnalytics/GoogleAnalytics';

export interface ListOfExistingDataset {
  allow_all_access: boolean;
  desc: string;
  name: string;
  owners: string[];
  tenant_id: string;
  _id: string;
  ad_tenant_id?: string;
  files: [];
  sharepoint_attributes?: SharePointAttributes;
  skill_id?: string;
  complex_documents?: boolean;
}
interface DatasetModalProps {
  open: boolean;
  onClose: () => void;
}
export const supportedFileTypes = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
  'message/rfc822', // Email
  '.msg', // Email
  'text/plain', // TXT
  'text/html', // HTML
  'text/htm', // HTM
  'application/pdf', // PDF
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // PPTX
];
export const supportedImageTypesForGeminiModels = ['image/png', 'image/jpeg', 'image/jpg'];
export const supportedImageTypesForGPTModels = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
export const unsupportedCustomAppTypes = ['video/mp4'];

const DatasetModal = ({ open, onClose }: DatasetModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { editDataset } = useAppSelector((state) => state.dataset);
  const { fileUpload, fileSelection, dataset, isSharepointSelected } = useAppSelector((state) => state.fileUpload);
  const [selection, setSelection] = useState<string>(editDataset.addFiles ? 'existing' : 'new');
  const [siteUrl, setSiteUrl] = useState('');
  const [workflowUrl, setWorkflowUrl] = useState('');
  const [includeSubfolder, setIncludeSubfolder] = useState<boolean>(true);
  const [libraryFolderPath, setLibraryFolderPath] = useState<string[]>(['Documents']);
  const [sharePointDatasetName, setSharePointDatasetName] = useState('');
  const [datasetRadioSelect, setdatasetRadioSelect] = useState<string>(
    editDataset.addFiles ? t('fileUpload.existingDataset') : t('fileUpload.newDataset')
  );

  const [isExistingDatasetListFetching, setIsExistingDatasetListFetching] = useState<boolean>(true);
  const [isExistingDatasetHasData, setIsExistingDatasetHasData] = useState<boolean>(false);
  const { addMoreFiles } = useAppSelector((state) => state.dataset);
  const [existingDatasetRadioSelect, setExistingDatasetRadioSelect] = useState(
    isSharepointSelected === AppSkillId.sharePoint
      ? t('fileUpload.existingSharePointDataset')
      : t('fileUpload.existingDataset')
  );
  const [showQueuedModal, setShowQueuedModal] = useState<boolean>(false);
  const [queuedDatasetName, setQueuedDatasetName] = useState<string>('');
  const [isSharePointDataset, setIsSharePointDataset] = useState<boolean>(false);
  const [isMainModalVisible, setIsMainModalVisible] = useState<boolean>(true);

  const restoreEditDatasetState = () => {
    dispatch(
      setEditDataset({
        addFiles: false,
        datasetId: '',
      })
    );
  };

  const handleClose = () => {
    setExistingDatasetRadioSelect(t('fileUpload.existingDataset'));
    setdatasetRadioSelect(t('fileUpload.newDataset'));
    setSelection('new');
    dispatch(toggleModal());
    dispatch(setIsSharePointSelected(''));
    if (editDataset.addFiles) restoreEditDatasetState();
    dispatch(updateDatasetState({ listOfUserOwnedDataset: [] }));
    dispatch(setFiles([]));
    dispatch(
      updateFileSelectionState({
        complexDocumentsEnabled: false,
      })
    );
    onClose();
  };

  const navigate = UseTrackedNavigate();
  const { isWelcomePage } = UsePageNavigation();

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      datasetName: '',
      datasetDescription: '',
    },
    mode: 'onChange',
  });
  useEffect(() => {
    if (open) {
      reset({
        datasetName: fileUpload.uploadedFiles.length > 0 ? fileUpload.uploadedFiles[0].name : '',
        datasetDescription: '',
      });
    }
  }, [open, fileUpload.uploadedFiles, reset]);

  const { isCustomAppSelected, selectedCustomAppSkill } = useAppSelector((state) => state.customApps.choosenCustomApp);
  useEffect(() => {
    if (fileUpload.uploadedFiles.length > 0) {
      modifyDatasetNameExists(getValues().datasetName);
    }
  }, [dataset.listOfUserOwnedDataset, isCustomAppSelected, fileUpload.uploadedFiles]);
  const validateFileUpload = (uploadedFiles: File[]) => {
    const BYTES_IN_KILOBYTE = 1024;
    const MAX_FILE_SIZE_MB = 100;
    const MAX_FILES = 10;

    // Check the number of files
    if (uploadedFiles.length > MAX_FILES) {
      enqueueSnackbar(t('fileUpload.uploadMax10Files'), { variant: 'error' });
      handleClose();
      return false;
    }

    // Calculate total size of uploaded files
    const TOTAL_FILES_SIZE_IN_BYTES = uploadedFiles.reduce((total, file) => total + file.size, 0);
    const TOTAL_FILES_SIZE_IN_MB = TOTAL_FILES_SIZE_IN_BYTES / (BYTES_IN_KILOBYTE * BYTES_IN_KILOBYTE);

    // Check the total size of files
    if (TOTAL_FILES_SIZE_IN_MB > MAX_FILE_SIZE_MB) {
      enqueueSnackbar(t('fileUpload.uploadMax100MB'), { variant: 'error' });
      handleClose();
      return false;
    }
    return true;
  };

  //When same file is uploaded multiple times the dataset name is added with suffix 1,2,3 etc.. to avoid confusion
  const handleDatasetNameSuffix = async () => {
    const file = fileUpload.uploadedFiles[0];
    const originalName = file['name'];

    // Extract file extension and base name
    const extensionIndex = originalName.lastIndexOf('.');
    const extension = extensionIndex > -1 ? originalName.slice(extensionIndex) : '';
    let baseName = extensionIndex > -1 ? originalName.slice(0, extensionIndex) : originalName;
    let finalName = originalName;
    let suffixCounter = 1;

    // Loop to find a unique name by adding a suffix if necessary
    while (checkDatasetNameExists(finalName)) {
      const suffix = `(${suffixCounter})`;
      const maxLengthForBaseName = MAX_LENGTH_DATASET_NAME - extension.length - suffix.length;
      baseName = baseName.substring(0, maxLengthForBaseName);
      finalName = `${baseName}${suffix}${extension}`;
      suffixCounter++;
    }
    return finalName;
  };

  const checkDatasetNameExists = (name: string) => {
    return (
      dataset.listOfUserOwnedDataset &&
      dataset.listOfUserOwnedDataset.some((existDataset) => existDataset.name === name)
    );
  };

  const modifyDatasetNameExists = async (name: string) => {
    const modifiedDatasetName = !checkDatasetNameExists(name) ? name : await handleDatasetNameSuffix();
    setValue('datasetName', modifiedDatasetName);
    return modifiedDatasetName;
  };

  const clearFileUploadAndFileSelectionStates = () => {
    dispatch(
      updateFileUploadState({
        uploadFileMessage: '',
        uploadFile: false,
      })
    );
    dispatch(
      updateFileSelectionState({
        responseMessage: false,
      })
    );
  };

  const handleFileUploadError = async (datasetId: string) => {
    clearFileUploadAndFileSelectionStates();
    enqueueSnackbar(t('fileUpload.fileUploadFail'), { variant: 'error' });
    await Api.deleteDataset(datasetId);
  };

  const readFiles = async () => {
    try {
      // Validate file size and number
      if (!validateFileUpload(fileUpload.uploadedFiles)) {
        return; //Doing a early Return
      }
      // Capture dataset name before clearing files (which triggers form reset)
      const capturedDatasetName = getValues().datasetName;
      
      // Close the modal
      handleClose();

      const checkIfUploadedFilesSupport = fileUpload.uploadedFiles.every((file) => {
        if (!file.type) {
          return file.name.endsWith('.msg');
        }
        return supportedFileTypes.includes(file.type);
      });
      if (!checkIfUploadedFilesSupport) {
        enqueueSnackbar(t('fileUpload.supportedFiles'), { variant: 'error' });
        return; //Return and stop processing further if unsupported files are uploaded.
      }

      dispatch(
        updateFileSelectionState({
          responseMessage: true,
        })
      );

      dispatch(
        updateFileUploadState({
          uploadFileMessage: t('fileUpload.uploadFileMessage.uploadStatus1'),
          uploadFile: true,
        })
      );

      if (isCustomAppSelected) {
        dispatch(setDisableCustomApps(true));
      }

      let datasetId: string = '';
      let activateDatasetName: string = '';
        let sowQuery;
        if (datasetRadioSelect === t('fileUpload.newDataset')) {
          const createdDataset = !isCustomAppSelected
          ? await Api.createDataset(
              capturedDatasetName,
              AppSkillId.DocCompletion,
              fileSelection.complexDocumentsEnabled
            )
          : await Api.createDataset(
              capturedDatasetName,
              selectedCustomAppSkill as SkillId,
              fileSelection.complexDocumentsEnabled
            );   

        datasetId = createdDataset.data._id;
        activateDatasetName = capturedDatasetName;
        
        
        try {
          await Api.uploadDataset(createdDataset.data._id, fileUpload.uploadedFiles);
        } catch {
          handleFileUploadError(createdDataset.data._id);
          return;
        }
        // Use activateDatasetName instead of getValues() to ensure we have the correct value
        dispatch(
          updateDatasetState({
            datasetName: activateDatasetName,
          })
        );
        dispatch(
          currentDataSet({
            currentDataSetName: activateDatasetName,
            currentDataSetId: datasetId,
          })
        );
      } else {
        datasetId = dataset.selectedDatasetId;
        activateDatasetName = dataset.datasetName;
        
        if (fileSelection.isReplaceFileCheckboxChecked) {
          // Filters out user uploaded files which are not in the selected dataset
          const filteredFile = fileUpload.uploadedFiles.filter(
            (file) => !fileUpload.listOfFilteredUploadedFilesExistInDataset.includes(file.name)
          );
          // Makes dataset ingest api call with the filtered files
          try {
            await Api.uploadDataset(datasetId, filteredFile);
          } catch {
            handleFileUploadError(datasetId);
            return;
          }
        } else {
          // Makes dataset ingest api call with all the user uploaded files
          try {
            await Api.uploadDataset(datasetId, fileUpload.uploadedFiles);
          } catch {
            handleFileUploadError(datasetId);
            return;
          }
        }

        dispatch(
          currentDataSet({
            currentDataSetName: dataset.datasetName,
            currentDataSetId: datasetId,
          })
        );
      }

      dispatch(
        updateFileUploadState({
          uploadFileMessage: t('fileUpload.uploadFileMessage.uploadStatus2'),
        })
      );
      
      // Use the dataset's skill_id for prepare, not hardcoded DocCompletion
      const prepare = await Api.prepareDataset(
        datasetId,
        AppSkillId.DocCompletion,
        fileSelection.isVisionSummaryEnabled
      );

      let workflowData = prepare.data;
      dispatch(
        updateFileUploadState({
          uploadFileMessage: t('fileUpload.uploadFileMessage.uploadStatus3'),
        })
      );
      const delay = 1000 * (fileUpload.uploadedFiles.length || 1);
      while (workflowData.status !== 'Completed') {
        const workflow = await Api.getWorkflowForDataset(datasetId, workflowData._id);
        if (workflow.data) {
          workflowData = workflow.data;
        }
        if (workflowData.status === 'Failed') {
          dispatch(
            updateFileUploadState({
              uploadFileMessage: t('fileUpload.uploadFileMessage.failurePrepareMesssage'),
            })
          );
          return;
        }
        await new Promise((r) => setTimeout(r, delay));
      }
      if (workflowData.status === 'Completed') {
        dispatch(
          updateFileUploadState({
            uploadFileMessage: t('fileUpload.uploadFileMessage.uploadStatus4'),
          })
        );
        // Fetch Custom Apps SOW response
        if (isCustomAppSelected) {
          //need to refactor the code
          const prepareSow = await Api.prepareDataset(datasetId, selectedCustomAppSkill as SkillId);
          // When custom app is sent for prepare, the dataset returns workflow_id instead of _id.
          // So the below sowWorkflowData has given type as any
          const sowWorkflowData: any = prepareSow.data;
          const delayedTime = 1000;
          const sowDelay = delayedTime * (fileUpload.uploadedFiles.length || 1);
          while (sowWorkflowData.status !== 'Completed') {
            const sowWorkflow = await Api.getWorkflowForDataset(datasetId, sowWorkflowData.workflow_id);
            if (sowWorkflow.data) {
              sowWorkflowData.status = sowWorkflow.data.status;
            }
            if (sowWorkflowData.status === 'Failed') {
              return;
            }
            await new Promise((r) => setTimeout(r, sowDelay));
          }
          sowQuery = (await Api.fetchSowQuery(datasetId, selectedCustomAppSkill as SkillId)).data;
        }
        dispatch(
          updateFileUploadState({
            uploadFileMessage: t('fileUpload.uploadFileMessage.uploadStatus5'),
          })
        );

        dispatch(
          updateFileUploadState({
            uploadFile: false,
          })
        );
        if (addMoreFiles) {
          dispatch(
            showUserActionContentOnChat(
              t('fileUpload.exisitingDatasetUpdateMessage', { fileName: dataset.datasetName })
            )
          );
        }
        dispatch(
          updateFileSelectionState({
            responseMessage: false,
          })
        );

        dispatch(
          updateDatasetState({
            makeActiveDataset: true,
            datasetName: activateDatasetName,
          })
        );

        dispatch(
          updateFileSelectionState({
            isVisionSummaryEnabled: false,
          })
        );
        //Dispatching the SOW response to Chat Box and Showing the active dataset name
        if (isCustomAppSelected) {
          dispatch(
            selectedDataSet({
              activeDataSetName: activateDatasetName,
              activeDataSetId: datasetId,
            })
          );

          dispatch(
            updateDatasetState({
              makeActiveDataset: false,
            })
          );

          dispatch(
            selectedCustomAppSkill === AppSkillId.AdvancedSummary
              ? saveAdvanceSummaryResponse(sowQuery as AdvanceSummaryResponse)
              : customAppsSkillIds.includes(selectedCustomAppSkill)
                ? saveSowCommitAiQueryResponse(sowQuery as SowCommitAiQueryResponse[])
                : saveSowPredictorQueryResponse(sowQuery as SowPredictorQueryResponse)
          );
          dispatch(setDisableCustomApps(false));
        }
        dispatch(updateDatasetState({ listOfUserOwnedDataset: [] }));
        // Refreshing the Datasets list after uploading a file.
        dispatch(refreshDatasetList());
      }
      if (isWelcomePage) {
        navigate(welcomePageNavigation);
      }
    } catch (error) {
      showSnackbarErrorMessage(error as ApiError);
    }
  };

  const sharePointUpdate = async () => {
    // For update flow, we can still use handleClose since it's updating existing dataset
    handleClose();
    dispatch(
      updateFileSelectionState({
        responseMessage: true,
      })
    );
    dispatch(
      updateFileUploadState({
        uploadFileMessage: t('fileUpload.uploadFileMessage.uploadStatus1'),
        uploadFile: true,
      })
    );
    let datasetId = dataset.selectedDatasetId;
    
    // For general purpose datasets, we create the SharePoint dataset normally
    // but remove the workflow URL immediately after prepare to stop document uploading
    const sharepoint_data = {
      site_url: siteUrl,
      power_automate_workflow_url: workflowUrl,
      include_subfolder: includeSubfolder,
      library_folder_path: libraryFolderPath,
    };
    
    await Api.updateSharePointDataset(datasetId, sharePointDatasetName, AppSkillId.sharePoint, sharepoint_data);
    dispatch(
      updateDatasetState({
        datasetName: sharePointDatasetName,
      })
    );
    dispatch(
      currentDataSet({
        currentDataSetName: sharePointDatasetName,
        currentDataSetId: datasetId,
      })
    );
    dispatch(
      updateFileUploadState({
        uploadFileMessage: t('fileUpload.uploadFileMessage.uploadStatus2'),
      })
    );
    // SharePoint datasets use DocCompletion skill_id
    const prepare = await Api.prepareDataset(datasetId, AppSkillId.DocCompletion);

      // All SharePoint datasets now stop after prepareDataset call (General purpose dataset behavior)
      // No more dataset type selection - always show queued modal
      {
        console.log('🚫 SharePoint document upload prevention - removing workflow URL');
        
        // CRITICAL: Immediately disable SharePoint workflow to prevent further document uploading
        try {
          await Api.updateSharePointDataset(
            datasetId, 
            sharePointDatasetName, 
            AppSkillId.sharePoint, 
            {
              site_url: siteUrl,
              power_automate_workflow_url: '', // Remove workflow URL to stop automation
            }
          );
          console.log('✅ Workflow URL removed - SharePoint automation stopped');
          
          // ENHANCED: Multiple checks at different intervals to catch any delayed uploads
          const checkAndRemoveDocuments = async (attempt: number) => {
            try {
              console.log(`🔍 Checking for documents (attempt ${attempt})...`);
              const datasetInfo = await Api.getDataset(datasetId);
              const dataset = datasetInfo.data as any;
              
              if (dataset && dataset.files && dataset.files.length > 0) {
                console.log(`⚠️ ALERT: Found ${dataset.files.length} uploaded documents despite prevention - removing immediately!`);
                
                // Immediately delete dataset and recreate
                await Api.deleteDataset(datasetId);
                console.log('🗑️ Deleted dataset with uploaded documents');
                
                // Recreate completely clean dataset
                const cleanSharepointData = {
                  site_url: siteUrl,
                  power_automate_workflow_url: '', // No workflow URL from the start
                  include_subfolder: includeSubfolder,
                  library_folder_path: libraryFolderPath,
                };
                
                const recreateDataset = await Api.createDataset(
                  sharePointDatasetName,
                  AppSkillId.sharePoint,
                  false,
                  cleanSharepointData
                );
                
                // Update datasetId to the new one
                datasetId = recreateDataset.data._id;
                
                // Update Redux state with new dataset ID
                dispatch(
                  currentDataSet({
                    currentDataSetName: sharePointDatasetName,
                    currentDataSetId: datasetId,
                  })
                );
                
                // Prepare the clean dataset (no workflow URL = no uploads)
                // SharePoint datasets use DocCompletion skill_id
                await Api.prepareDataset(datasetId, AppSkillId.DocCompletion);
                
                console.log('✅ Dataset recreated without SharePoint documents - ID:', datasetId);
                return true; // Found and removed documents
              } else {
                console.log(`✅ No documents found in attempt ${attempt}`);
                return false; // No documents found
              }
            } catch (checkError) {
              console.warn(`Warning: Document check attempt ${attempt} failed:`, checkError);
              return false;
            }
          };
          
          // Check immediately
          const found1 = await checkAndRemoveDocuments(1);
          
          // Wait and check again after 2 seconds
          await new Promise(resolve => setTimeout(resolve, 2000));
          const found2 = await checkAndRemoveDocuments(2);
          
          // Wait and check again after 5 seconds 
          await new Promise(resolve => setTimeout(resolve, 3000));
          const found3 = await checkAndRemoveDocuments(3);
          
          // Final check after 10 seconds
          await new Promise(resolve => setTimeout(resolve, 5000));
          const found4 = await checkAndRemoveDocuments(4);
          
          if (!found1 && !found2 && !found3 && !found4) {
            console.log('✅ SUCCESS: No SharePoint documents uploaded - prevention worked!');
          } else {
            console.log('⚠️ Documents were found and removed - check logs above for details');
          }
        } catch (cleanupError) {
          console.warn('Warning: Could not remove workflow URL:', cleanupError);
        }
        // Stop all file upload states
        dispatch(
          updateFileUploadState({
            uploadFile: false,
            uploadFileMessage: 'Dataset queued for processing - SharePoint automation stopped',
          })
        );
        dispatch(
          updateFileSelectionState({
            responseMessage: false,
            isVisionSummaryEnabled: false,
          })
        );

        // Update dataset state (but don't refresh list to avoid triggering API calls)
        // Use successResponseModal for queued datasets to show only success message
        dispatch(updateDatasetState({ 
          listOfUserOwnedDataset: [],
          datasetName: sharePointDatasetName,
        }));
        dispatch(setSuccessResponseModal(true));
        
        // Set the queued dataset name and show modal together with flushSync
        // This ensures both state updates happen before the modal renders
        flushSync(() => {
          setQueuedDatasetName(sharePointDatasetName);
          setIsSharePointDataset(true);
          setShowQueuedModal(true);
        });
        
        // CRITICAL: Early return to stop ALL subsequent API calls including workflow processing
        // This prevents: Api.getWorkflowForDataset, workflow polling, SharePoint document processing, etc.
        // NO SHAREPOINT DOCUMENTS WILL BE UPLOADED after this point
        return;
      }
  };

  const sharePointExecution = async () => {
    if (siteUrl.trim() === '' || workflowUrl.trim() === '' || sharePointDatasetName.trim() === '') {
      enqueueSnackbar(t('fileUpload.uploadFileMessage.emptySharepointAttributes'), {
        variant: 'error',
      });
      return;
    }
    if (!isValidUrl(siteUrl)) {
      enqueueSnackbar(t('fileUpload.validSIteURL'), { variant: 'error' });
      return;
    }

    if (!isValidUrl(workflowUrl)) {
      enqueueSnackbar(t('fileUpload.validWorkflowURL'), { variant: 'error' });
      return;
    }
    try {
      // Close the main modal IMMEDIATELY when submit button is clicked (before API calls)
      // Use local state to hide main modal while keeping component mounted for queued modal
      setIsMainModalVisible(false);
      dispatch(
        updateFileSelectionState({
          responseMessage: true,
        })
      );
      dispatch(
        updateFileUploadState({
          uploadFileMessage: t('fileUpload.uploadFileMessage.uploadStatus1'),
          uploadFile: true,
        })
      );

      let datasetId: string = '';

      // For general purpose datasets, we create the SharePoint dataset normally
      // but remove the workflow URL immediately after prepare to stop document uploading
      const sharepoint_data = {
        site_url: siteUrl,
        power_automate_workflow_url: workflowUrl,
        include_subfolder: includeSubfolder,
        library_folder_path: libraryFolderPath,
      };

      const createDataset = await Api.createDataset(
        sharePointDatasetName,
        AppSkillId.sharePoint,
        false,
        sharepoint_data
      );
      datasetId = createDataset.data._id;
      
      
      dispatch(
        updateDatasetState({
          datasetName: sharePointDatasetName,
        })
      );
      dispatch(
        currentDataSet({
          currentDataSetName: sharePointDatasetName,
          currentDataSetId: datasetId,
        })
      );
      dispatch(
        updateFileUploadState({
          uploadFileMessage: t('fileUpload.uploadFileMessage.uploadStatus2'),
        })
      );
      // Use the actual skill_id from the dataset
      const prepare = await Api.prepareDataset(datasetId, AppSkillId.DocCompletion);

      // All SharePoint datasets now stop after prepareDataset call (General purpose dataset behavior)
      // No more dataset type selection - always show queued modal
      // Always early return for SharePoint datasets (removed isSharepointSelected check)
      {
        // CRITICAL: Stop ALL SharePoint document processing here
        // This prevents any SharePoint site access, document uploading, or workflow URL calls
        
        // Stop all file upload states
        dispatch(
          updateFileUploadState({
            uploadFile: false,
            uploadFileMessage: 'Dataset queued for processing - No SharePoint document uploading',
          })
        );
        dispatch(
          updateFileSelectionState({
            responseMessage: false,
            isVisionSummaryEnabled: false,
          })
        );

        // Update dataset state (but don't refresh list to avoid triggering API calls)
        // Use successResponseModal for queued datasets to show only success message
        dispatch(updateDatasetState({ 
          listOfUserOwnedDataset: [],
          datasetName: sharePointDatasetName,
        }));
        dispatch(setSuccessResponseModal(true));
        
        // Reset internal state first
        setSelection('new');
        dispatch(setIsSharePointSelected(''));
        if (editDataset.addFiles) restoreEditDatasetState();
        dispatch(setFiles([]));
        dispatch(
          updateFileSelectionState({
            complexDocumentsEnabled: false,
          })
        );
        
        // Set the queued dataset name and show modal together with flushSync
        // This ensures both state updates happen before the modal renders
        flushSync(() => {
          setQueuedDatasetName(sharePointDatasetName);
          setIsSharePointDataset(true);
          setShowQueuedModal(true);
        });
        
        // CRITICAL: Early return to stop ALL subsequent API calls including workflow processing
        // This prevents: Api.getWorkflowForDataset, workflow polling, SharePoint document processing, etc.
        // NO SHAREPOINT DOCUMENTS WILL BE UPLOADED after this point
        return;
      }

      let workflowData = prepare.data;

      dispatch(
        updateFileUploadState({
          uploadFileMessage: t('fileUpload.uploadFileMessage.uploadStatus3'),
        })
      );
      while (workflowData.status !== 'Completed') {
        const workflow = await Api.getWorkflowForDataset(datasetId, workflowData._id);
        if (workflow.data) {
          workflowData = workflow.data;
        }
        if (workflowData.status === 'Failed') {
          dispatch(
            updateFileUploadState({
              uploadFileMessage: t('fileUpload.uploadFileMessage.failurePrepareMesssage'),
            })
          );
          return;
        }
        await new Promise((r) => setTimeout(r, 10000));
      }
      if (workflowData.status === 'Completed') {
        dispatch(
          updateFileUploadState({
            uploadFileMessage: t('fileUpload.uploadFileMessage.uploadStatus4'),
          })
        );
        // Fetch Custom Apps SOW response
        dispatch(
          updateFileUploadState({
            uploadFileMessage: t('fileUpload.uploadFileMessage.uploadStatus5'),
          })
        );

        dispatch(
          updateFileUploadState({
            uploadFile: false,
          })
        );

        dispatch(
          updateFileSelectionState({
            responseMessage: false,
          })
        );

        dispatch(
          updateDatasetState({
            makeActiveDataset: true,
          })
        );
        dispatch(updateDatasetState({ listOfUserOwnedDataset: [] }));
        // Refreshing the Datasets list after uploading a file.
        dispatch(refreshDatasetList());
        if (isWelcomePage) {
          navigate(chatPageNavigation);
        }
      }
    } catch (e) {
      enqueueSnackbar(t('fileUpload.uploadFileMessage.failureSharepointError'), { variant: 'error' });
      dispatch(
        updateFileSelectionState({
          responseMessage: false,
        })
      );
      dispatch(
        updateFileUploadState({
          uploadFileMessage: '',
          uploadFile: false,
        })
      );
    }
  };

  const fileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    // Restrict .mp4 for custom app
    if (isCustomAppSelected) {
      const hasUnsupported = Array.from(files!).some((f) => unsupportedCustomAppTypes.includes(f.type));
      if (hasUnsupported) {
        enqueueSnackbar(t('rightPanel.customapps.customAppSupportedFiles'), { variant: 'error' });
        e.target.value = '';
        return;
      }
    }

    if (!files || files.length === 0) return;

    // Check for unsupported file types (not in supportedFileTypes)
    const unsupportedFiles = Array.from(files).filter(
      (file) => !supportedFileTypes.includes(file.type) && !supportedFileTypes.includes(file.name.split('.').pop()?.toLowerCase() || '')
    );
    if (unsupportedFiles.length > 0) {
      enqueueSnackbar(
        `Unsupported file type(s): ${unsupportedFiles.map((f) => f.name).join(', ')}. Please select only supported files.`,
        { variant: 'error' }
      );
      e.target.value = '';
      return;
    }

    if (findEmptyFileName(files!)) {
      enqueueSnackbar(t('fileUpload.fileIsEmpty', { fileName: findEmptyFileName(files!) }), {
        variant: 'error',
      });
      return;
    }
    dispatch(setFiles(Array.from(files as FileList)));
    dispatch(toggleModal());
    e.target.value = '';
  };

  const handleInitialComponentData = async () => {
    try {
      let listOfExistingDataset;
      if (isCustomAppSelected) {
        listOfExistingDataset = (await Api.listDatasets(true, true, selectedCustomAppSkill as SkillId))
          .data as ListOfExistingDataset[];
      } else {
        listOfExistingDataset = (await Api.listDatasets(true, false, AppSkillId.DocCompletion))
          .data as ListOfExistingDataset[];
      }
      dispatch(
        updateDatasetState({
          listOfUserOwnedDataset: listOfExistingDataset,
        })
      );
    } catch (error) {
      showSnackbarErrorMessage(error as ApiError);
    } finally {
      setIsExistingDatasetListFetching(false);
    }
  };

  useEffect(() => {
    handleInitialComponentData();
  }, [isCustomAppSelected]);

  useEffect(() => {
    if (open) {
      setIsMainModalVisible(true); // reset visibility whenever modal is opened
    }
  }, [open]);

  useEffect(() => {
    modifyDatasetNameExists(getValues().datasetName);
  }, [dataset.listOfUserOwnedDataset, isCustomAppSelected]);

  const acceptFileTypes = isCustomAppSelected
    ? supportedFileTypes.filter((type) => !unsupportedCustomAppTypes.includes(type)).join(',')
    : supportedFileTypes.join(',');

  return (
    <>
    <Modal open={open && isMainModalVisible} onClose={handleClose}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          minHeight: 450,
          maxWidth: '60vw',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 6,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <Typography
            component="h2"
            variant="h2"
            role="heading"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              fontWeight: '700',
              fontSize: '24px',
              lineHeight: '24px',
            }}
          >
            {selection === 'existing'
              ? t('fileUpload.updatedataset')
              : datasetRadioSelect === t('fileUpload.newDataset')
                ? t('fileUpload.newdataset')
                : datasetRadioSelect === t('fileUpload.sharePointDataset')
                  ? t('fileUpload.sharePointDataset')
                  : t('fileUpload.updatedataset')}
          </Typography>
          <Typography sx={{ borderTop: '1px dashed grey' }}></Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
            <Button
              variant={selection === 'new' || selection === '' ? 'contained' : 'outlined'}
              onClick={() => {
                LogButtonEventToGA(t('fileUpload.new'));
                setSelection('new');
                setdatasetRadioSelect(t('fileUpload.newDataset'));
                dispatch(setFiles([]));
              }}
              sx={{ marginRight: 2 }}
            >
              {t('fileUpload.new')}
            </Button>
            <Button
              variant={selection === 'existing' ? 'contained' : 'outlined'}
              onClick={() => {
                LogButtonEventToGA(t('fileUpload.existing'));
                setSelection('existing');
                setdatasetRadioSelect(t('fileUpload.existingDataset'));
                dispatch(setFiles([]));
              }}
              disabled={isCustomAppSelected}
            >
              {t('fileUpload.existing')}
            </Button>
          </Box>

          {selection === 'new' || selection === '' ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                marginTop: `1rem`,
              }}
            >
              <RadioSelect datasetRadioSelect={datasetRadioSelect} setdatasetRadioSelect={setdatasetRadioSelect} />
              {datasetRadioSelect === t('fileUpload.newDataset') ? (
                <NewDataset formValidation={{ control, errors, reset }} />
              ) : (
                <SharePointDataset
                  setSiteUrl={setSiteUrl}
                  setWorkflowUrl={setWorkflowUrl}
                  setSharePointDatasetName={setSharePointDatasetName}
                  setIncludeSubfolder={setIncludeSubfolder}
                  setLibraryFolderPath={setLibraryFolderPath}
                />
              )}
            </Box>
          ) : (
            <Box>
              <ExistingDatasetRadioSelect
                existingDatasetRadioSelect={existingDatasetRadioSelect}
                setExistingDatasetRadioSelect={setExistingDatasetRadioSelect}
                siteUrl={siteUrl}
                isExistingDatasetHasData={isExistingDatasetHasData}
              />
              {existingDatasetRadioSelect === t('fileUpload.existingSharePointDataset') ? (
                <ExistingSharePointDataset
                  setSiteUrl={setSiteUrl}
                  setWorkflowUrl={setWorkflowUrl}
                  setSharePointDatasetName={setSharePointDatasetName}
                  setIncludeSubfolder={setIncludeSubfolder}
                  setLibraryFolderPath={setLibraryFolderPath}
                />
              ) : (
                <ExistingDataset
                  isExistingDatasetListFetching={isExistingDatasetListFetching}
                  setIsExistingDatasetHasData={setIsExistingDatasetHasData}
                />
              )}
            </Box>
          )}
        </Box>

        {((selection === 'new' && datasetRadioSelect === t('fileUpload.newDataset')) ||
          (selection === 'existing' && existingDatasetRadioSelect !== t('fileUpload.existingSharePointDataset'))) && (
          <Box margin={'1rem 0 1.5rem'} display={'flex'}>
            <Button variant="contained" component="label" startIcon={<CloudUploadIcon />} sx={{ marginRight: '1rem' }}>
              {t('fileUpload.uploadFile').toUpperCase()}
              <input type="file" multiple onChange={fileInput} hidden accept={acceptFileTypes} />
            </Button>
            <Box display={'flex'} justifyContent={'space-between'}>
              <Typography fontSize={16} color={'#636363'} sx={{ alignSelf: 'center' }}>
                {t('fileUpload.numberofFilesSelected')}
                {fileUpload.uploadedFiles.length}
              </Typography>
            </Box>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '1.5rem',
            mt: '1.5rem',
          }}
        >
          <Button
            onClick={() => {
              LogButtonEventToGA(t('rightPanel.cancel')); // Log the cancel button click event
              handleClose();
            }}
            sx={{ textDecoration: 'underline' }}
          >
            {t('rightPanel.cancel')}
          </Button>

          {selection === 'new' && (
            <Button
              sx={{
                fontSize: '14px',
                boxShadow: 'none',
                height: '2rem',
                '&:hover': { boxShadow: 'none' },
              }}
              variant="contained"
              onClick={
                datasetRadioSelect === t('fileUpload.newDataset')
                  ? handleSubmit(readFiles)
                  : handleSubmit(sharePointExecution)
              }
              disabled={
                datasetRadioSelect === t('fileUpload.newDataset')
                  ? !getValues().datasetName || fileUpload.uploadedFiles.length === 0
                  : !siteUrl || !workflowUrl || !sharePointDatasetName
              } //Disabled the button if the fields are empty
            >
              {t('fileUpload.createdataset')}
            </Button>
          )}
          {selection === 'existing' && (
            <Button
              sx={{
                fontSize: '14px',
                boxShadow: 'none',
                height: '2rem',
                '&:hover': { boxShadow: 'none' },
              }}
              variant="contained"
              onClick={
                existingDatasetRadioSelect === t('fileUpload.existingSharePointDataset')
                  ? handleSubmit(sharePointUpdate)
                  : existingDatasetRadioSelect === t('fileUpload.existingDataset')
                    ? handleSubmit(readFiles)
                    : handleSubmit(sharePointExecution)
              }
              disabled={
                existingDatasetRadioSelect === t('fileUpload.existingSharePointDataset')
                  ? !siteUrl || !workflowUrl || !sharePointDatasetName
                  : fileUpload.uploadedFiles.length === 0
              } //Disabled the button if the fields are empty
            >
              {t('fileUpload.updatedataset')}
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
    {/* Queued Dataset Modal */}
    <Modal 
      open={showQueuedModal}
      onClose={() => {
        // If queued modal is closed via backdrop/escape, fully reset and allow reopen
        setShowQueuedModal(false);
        setIsMainModalVisible(true);
        dispatch(setOpenCloseDatasetModal(false));
        handleClose();
      }}
      disableAutoFocus
      disableEnforceFocus
      keepMounted={false}
      disablePortal={false}
      hideBackdrop={true}
      aria-labelledby="queued-dataset-modal-title"
      aria-describedby="queued-dataset-modal-description"
      sx={{
        zIndex: 1501, // Much higher than default Modal z-index (1300) to appear above main modal
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          zIndex: 1401,
        }}
      >
        <Typography
          id="queued-dataset-modal-title"
          component="h2"
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '20px',
            marginBottom: 2,
            textAlign: 'center',
          }}
        >
          Dataset Queued
        </Typography>
        <Typography
          id="queued-dataset-modal-description"
          sx={{
            marginBottom: 3,
            textAlign: 'center',
            color: '#505050',
          }}
        >
          Your dataset has been queued for processing.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={() => {
              setShowQueuedModal(false);
              
              dispatch(refreshDatasetList());
              
              // Reset SharePoint flag
              setIsSharePointDataset(false);
              
              // Close the main modal and unmount
              dispatch(setOpenCloseDatasetModal(false));
              handleClose();
              
              // Success message will remain visible on chat
              // Don't navigate immediately - let user see the success message
            }}
            sx={{
              textTransform: 'none',
            }}
          >
            OK
          </Button>
        </Box>
      </Box>
    </Modal>
    </>
  );
};

export default DatasetModal;
